import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, RotateCcw, Save, Stamp, Trash2, Eye } from "lucide-react";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { Spinner } from "../common";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { DEFAULT_SETTINGS, OPACITY_MAX, OPACITY_MIN, POSITIONS, POSITION_LABELS, SIZE_MAX, SIZE_MIN, normalizeSettings } from "./watermarkLayout";
import { fetchWatermarkSettings, watermarkLogoUrl } from "./WatermarkOverlay";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

const DOCS = [
  { key: "receipt", label: "Payment receipt" },
  { key: "notice", label: "Demand notice" },
  { key: "slip", label: "Demand slip (bulk print)" },
];
const ACCEPT = "image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp";
const MAX_LOGO_BYTES = 3 * 1024 * 1024;
// the 3 x 3 placement pad: which cell is which anchor (null = not selectable)
const PAD = [["top-left", "top", "top-right"], [null, "center", null], ["bottom-left", "bottom", "bottom-right"]];

/** Page 1 of a PDF blob drawn on a canvas with pdf.js (fits the container width). */
function PdfFirstPage({ blob, testId = "watermark-preview-canvas" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!blob) return undefined;
    let alive = true;
    let task = null;
    let doc = null;
    setFailed(false);
    blob.arrayBuffer()
      .then((buf) => { task = pdfjs.getDocument({ data: new Uint8Array(buf) }); return task.promise; })
      .then(async (d) => {
        doc = d;
        const page = await d.getPage(1);
        if (!alive || !canvasRef.current) return;
        const width = Math.max(280, wrapRef.current?.clientWidth || 600);
        const base = page.getViewport({ scale: 1 });
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const scale = width / base.width;
        const vp = page.getViewport({ scale: scale * dpr });
        const canvas = canvasRef.current;
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = `${Math.floor(vp.width / dpr)}px`;
        canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; try { task?.destroy?.(); } catch { /* ignore */ } try { doc?.destroy?.(); } catch { /* ignore */ } };
  }, [blob]);
  return (
    <div ref={wrapRef} className="w-full">
      {failed
        ? <p className="text-sm text-muted-foreground p-4" data-testid="watermark-preview-failed">The preview could not be drawn in this browser. Save and open a receipt or notice PDF to check it.</p>
        : <canvas ref={canvasRef} data-testid={testId} className="block w-full rounded-md border bg-white shadow-sm" />}
    </div>
  );
}

/**
 * Phase 224 - Super Admin → Tenant Config → "Watermark" tab: the selected municipality's logo watermark for demand
 * notices, bulk demand slips and payment receipts (on screen + PDF). Everything is previewed live on a real sample
 * PDF and nothing changes until Save.
 */
export function SuperAdminWatermarkPanel({ code }) {
  const [saved, setSaved] = useState(null);          // server state (settings + logo meta)
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [logoFile, setLogoFile] = useState(null);     // a new logo chosen but not saved yet
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [doc, setDoc] = useState("receipt");
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewApplied, setPreviewApplied] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const reqRef = useRef(0);

  const load = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/watermark`)
      .then((r) => { setSaved(r.data); setForm(normalizeSettings(r.data)); })
      .catch((e) => { toast.error(errMsg(e)); setSaved({ has_logo: false, saved: false }); });
  }, [code]);
  useEffect(() => { setSaved(null); setLogoFile(null); setRemoveLogo(false); setPreviewBlob(null); load(); }, [load]);

  useEffect(() => {
    if (!logoFile) { setLogoPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const hasLogo = !!logoFile || (!!saved?.has_logo && !removeLogo);
  const dirty = useMemo(() => {
    if (!saved) return false;
    const base = normalizeSettings(saved);
    return !!logoFile || removeLogo ||
      ["enabled", "position", "repeat", "size_pct", "opacity_pct", "color_mode"].some((k) => base[k] !== form[k]);
  }, [saved, form, logoFile, removeLogo]);

  // live preview - debounced, always the latest request wins
  useEffect(() => {
    if (!saved) return undefined;
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const fd = new FormData();
        fd.append("settings", JSON.stringify({ ...form, enabled: form.enabled && !removeLogo }));
        if (logoFile) fd.append("logo", logoFile);
        const r = await api.post(`/platform/municipalities/${code}/watermark/preview?doc=${doc}`, fd, { responseType: "blob" });
        if (reqRef.current !== id) return;
        setPreviewBlob(r.data);
        setPreviewApplied(String(r.headers?.["x-watermark-applied"] || "") === "1" && !removeLogo);
      } catch (e) {
        if (reqRef.current === id) toast.error(errMsg(e));
      } finally {
        if (reqRef.current === id) setPreviewBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [code, saved, form, logoFile, removeLogo, doc]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) return toast.error("Upload a PNG, JPG or WebP logo (PNG with a transparent background looks best)");
    if (f.size > MAX_LOGO_BYTES) return toast.error("The logo must be smaller than 3 MB");
    setLogoFile(f);
    setRemoveLogo(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("settings", JSON.stringify(form));
      if (logoFile) fd.append("logo", logoFile);
      if (removeLogo && !logoFile) fd.append("remove_logo", "1");
      const r = await api.put(`/platform/municipalities/${code}/watermark`, fd);
      setSaved(r.data); setForm(normalizeSettings(r.data)); setLogoFile(null); setRemoveLogo(false);
      fetchWatermarkSettings(code, true);
      toast.success(`Watermark saved for ${r.data.municipality || code.toUpperCase()}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!saved) return <Spinner />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const currentLogoUrl = logoPreviewUrl || (saved.has_logo && !removeLogo ? watermarkLogoUrl(code, saved.logo_updated_at) : null);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="watermark-panel">
      <div className="space-y-4">
        <Card className="shadow-sm" data-testid="watermark-logo-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2"><Stamp className="h-4 w-4" /> Municipality logo</CardTitle>
            <CardDescription>
              This logo is printed in the header beside the municipality's name on every demand notice, bulk demand slip and
              payment receipt (on screen and in the PDF), and - when switched on below - as a faint watermark behind the
              page. A PNG with a transparent background looks best.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-24 w-24 rounded-md border bg-[linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%),linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px] flex items-center justify-center overflow-hidden"
                data-testid="watermark-logo-thumb">
                {currentLogoUrl
                  ? <img src={currentLogoUrl} alt="Municipality logo" className="max-h-full max-w-full object-contain" />
                  : <span className="text-[11px] text-muted-foreground text-center px-2">No logo yet</span>}
              </div>
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={pickFile} data-testid="watermark-logo-input" />
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="watermark-logo-upload">
                    <ImagePlus className="h-4 w-4 mr-1.5" /> {hasLogo ? "Replace logo" : "Upload logo"}
                  </Button>
                  {hasLogo && (
                    <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" data-testid="watermark-logo-remove"
                      onClick={() => { setLogoFile(null); setRemoveLogo(true); }}>
                      <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground" data-testid="watermark-logo-status">
                  {logoFile ? <>New logo <span className="font-medium text-foreground">{logoFile.name}</span> - not saved yet</>
                    : removeLogo ? <span className="text-destructive">Logo will be removed when you save - no watermark will be printed</span>
                      : saved.has_logo ? <>Saved logo {saved.logo_name ? <span className="font-medium text-foreground">{saved.logo_name}</span> : null}{saved.logo_w ? ` · ${saved.logo_w} x ${saved.logo_h} px` : ""}</>
                        : "Without a logo nothing is printed - upload one to get the header logo and the watermark."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm" data-testid="watermark-options-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base font-heading">Watermark options</CardTitle>
                <CardDescription>Changes show in the preview straight away; nothing is applied until you save.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="wm-enabled" className="text-sm" data-testid="watermark-enabled-state">{form.enabled ? "On" : "Off"}</Label>
                <Switch id="wm-enabled" checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="watermark-enabled-switch" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm">How many logos</Label>
                <ToggleGroup type="single" value={form.repeat} onValueChange={(v) => v && set("repeat", v)} className="justify-start" variant="outline" data-testid="watermark-repeat">
                  <ToggleGroupItem value="single" data-testid="watermark-repeat-single">One logo</ToggleGroupItem>
                  <ToggleGroupItem value="repeat" data-testid="watermark-repeat-repeat">Repeated across the page</ToggleGroupItem>
                </ToggleGroup>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Colour</Label>
                <ToggleGroup type="single" value={form.color_mode} onValueChange={(v) => v && set("color_mode", v)} className="justify-start" variant="outline" data-testid="watermark-color">
                  <ToggleGroupItem value="grey" data-testid="watermark-color-grey">Grey</ToggleGroupItem>
                  <ToggleGroupItem value="original" data-testid="watermark-color-original">Original colours</ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Where the logo sits</Label>
                <span className="text-xs text-muted-foreground" data-testid="watermark-position-label">
                  {form.repeat === "repeat" ? "Repeated logos cover the whole page" : POSITION_LABELS[form.position]}
                </span>
              </div>
              <div className={`grid grid-cols-3 gap-1.5 w-fit ${form.repeat === "repeat" ? "opacity-50 pointer-events-none" : ""}`} role="radiogroup" aria-label="Logo position" data-testid="watermark-position-pad">
                {PAD.flat().map((p, i) => p ? (
                  <button key={p} type="button" role="radio" aria-checked={form.position === p} aria-label={POSITION_LABELS[p]} title={POSITION_LABELS[p]}
                    onClick={() => set("position", p)} data-testid={`watermark-position-${p}`}
                    className={`h-10 w-14 rounded-md border text-[10px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${form.position === p ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}>
                    {POSITION_LABELS[p].replace(" corner", "")}
                  </button>
                ) : <span key={`blank-${i}`} className="h-10 w-14" />)}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Logo size</Label>
                  <span className="text-xs font-mono" data-testid="watermark-size-value">{form.size_pct}% of the page width</span>
                </div>
                <Slider min={SIZE_MIN} max={SIZE_MAX} step={1} value={[form.size_pct]} onValueChange={([v]) => set("size_pct", v)} data-testid="watermark-size-slider" aria-label="Logo size" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Strength (transparency)</Label>
                  <span className="text-xs font-mono" data-testid="watermark-opacity-value">{form.opacity_pct}%</span>
                </div>
                <Slider min={OPACITY_MIN} max={OPACITY_MAX} step={1} value={[form.opacity_pct]} onValueChange={([v]) => set("opacity_pct", v)} data-testid="watermark-opacity-slider" aria-label="Watermark strength" />
                <p className="text-[11px] text-muted-foreground">Capped at {OPACITY_MAX}% so the text always stays easy to read.</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap pt-1 border-t">
              <p className="text-xs text-muted-foreground" data-testid="watermark-saved-meta">
                {saved.saved
                  ? <>Saved {saved.updated_at ? fmtDateTime(saved.updated_at) : ""}{saved.updated_by ? ` by ${saved.updated_by}` : ""}</>
                  : <>Nothing saved yet - the default is one faint grey logo in the centre.</>}
                {dirty && <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300">Unsaved changes</Badge>}
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={() => setForm({ ...DEFAULT_SETTINGS })} data-testid="watermark-reset-defaults">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Default
                </Button>
                <Button type="button" onClick={save} disabled={saving || !dirty} data-testid="watermark-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm lg:sticky lg:top-4 self-start" data-testid="watermark-preview-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base font-heading flex items-center gap-2"><Eye className="h-4 w-4" /> Live preview</CardTitle>
            <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="watermark-preview-status">
              {previewBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…</>
                : previewApplied === false ? (hasLogo ? "Header logo only (watermark switched off)" : "No logo - nothing printed") : previewApplied ? "Header logo + watermark applied" : ""}
            </span>
          </div>
          <Tabs value={doc} onValueChange={setDoc}>
            <TabsList data-testid="watermark-preview-doc-tabs">
              {DOCS.map((d) => <TabsTrigger key={d.key} value={d.key} data-testid={`watermark-preview-doc-${d.key}`}>{d.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {previewBlob ? <PdfFirstPage blob={previewBlob} /> : <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">Preparing preview…</div>}
          <p className="text-[11px] text-muted-foreground mt-2">This is the real PDF page as citizens and staff will download it; the on-screen receipt uses the same settings.</p>
        </CardContent>
      </Card>
    </div>
  );
}
